import {
  compact,
  concat,
  filter,
  flatten,
  get,
  has,
  isObject,
  omit,
  values
} from 'lodash'
import * as FormActions from '../actions/Forms'
import * as ModalActions from '../actions/Modals'
import { gaEvent } from '../common/services/analytics'
import { deleteItem, postJSON } from '../common/services/api'
import { buildEncodedForm, getObjectDiff } from '../common/utils'
import {
  isDependentFieldHidden,
  validateForm,
  validateSingleField
} from '../common/validation/functions'
import * as DataOperations from './Data'

export function deleteFormItem({ id, list, fetchQuery, onComplete }) {
  return (dispatch) => {
    return deleteItem(id, list)
      .then((res) => {
        dispatch(DataOperations.loadList(list, fetchQuery))
        gaEvent({
          category: 'Forms',
          action: `Deleted ${list} item`
        })
        if (onComplete) onComplete()
        return res
      })
      .catch((err) => {
        gaEvent({
          category: 'Forms',
          action: `Error deleting ${list} item`,
          label: err.message || err.error
        })
        console.log(err)
      })
  }
}

export function validateField({ key, formId }) {
  return (dispatch, getState) => {
    const forms = getState().forms
    if (!forms[formId]) {
      return
    }
    const { fields, files, fieldValidation, validationErrors } = forms[formId]

    const value = getFormValue(key, fields, files)
    const validationProperties = fieldValidation[key]

    // Object validation types are objects not strings, so related keys can be passed in as part of the object.
    // For an example of how this works, see the endDate field in the EmarMedicationStartEndSection.
    const objectValidationTypes =
      validationProperties &&
      filter(validationProperties.validationTypes, (type) => {
        return isObject(type)
      })

    const relatedKeys =
      validationProperties &&
      compact(
        concat(
          validationProperties.relatedKeys,
          flatten(objectValidationTypes.map((type) => type.relatedKeys))
        )
      )
    const relatedValues =
      relatedKeys && relatedKeys.length
        ? relatedKeys.map((key) => getFormValue(key, fields, files))
        : null

    if (!validationProperties) {
      return
    }

    const result = validateSingleField({
      key,
      relatedValues,
      value,
      validationProperties
    })
    if (result.validationError) {
      dispatch(
        FormActions.setValidationErrors({
          errors: {
            ...validationErrors,
            [key]: result.validationError
          },
          formId
        })
      )
    } else {
      dispatch(
        FormActions.setValidationErrors({
          errors: omit(validationErrors, key),
          formId
        })
      )
    }
  }
}

export function validateFormFields({ formId, onComplete, onError }) {
  return async (dispatch, getState) => {
    const { fields, fieldValidation, files } = getState().forms[formId]
    try {
      await validateForm({ ...fields, ...files }, fieldValidation)
      dispatch(FormActions.setValidationErrors({ formId }))
      dispatch(FormActions.updateFormErrors({ formId }))
      if (onComplete) onComplete()
    } catch (e) {
      if (e.errors) {
        dispatch(
          FormActions.setValidationErrors({
            errors: e.errors,
            formId
          })
        )
        dispatch(
          FormActions.updateFormErrors({
            errors: values(e.errors),
            formId
          })
        )
      }
      if (onError) onError()
    }
  }
}

export function postItem({
  formId,
  json,
  keepFormOpen,
  method,
  onComplete,
  onError,
  url
}) {
  return async (dispatch, getState) => {
    const { fields, fieldValidation, files, initial } = getState().forms[formId]

    try {
      await validateForm({ ...fields, ...files }, fieldValidation)
      dispatch(FormActions.setValidationErrors({ formId }))
      gaEvent({
        category: 'Forms',
        action: `Saved a ${formId} form`
      })
    } catch (e) {
      if (e.errors) {
        dispatch(
          FormActions.setValidationErrors({
            errors: e.errors,
            formId
          })
        )
        dispatch(
          FormActions.updateFormErrors({
            errors: values(e.errors),
            formId
          })
        )
        gaEvent({
          category: 'Forms',
          action: `Validation errors for ${formId}`,
          label: JSON.stringify(e.errors)
        })
        if (onError) onError()
      }
      return null
    }

    dispatch(clearHiddenDependentFields({ fields, fieldValidation, formId }))

    const updatedFields = getState().forms[formId].fields
    const changes = getObjectDiff(updatedFields, initial)

    const body = populateNestedFields({ changes, fields: updatedFields })
    let req
    if (Object.keys(files).length) {
      const formData = buildEncodedForm({ ...body, ...files })
      req = {
        url,
        method,
        body: formData,
        contentType: 'multipart/form-data'
      }
    } else {
      req = {
        url,
        method,
        json: json || body,
        contentType: 'application/json'
      }
    }
    dispatch(postFormData(req, { formId, keepFormOpen, onComplete, onError }))
  }
}

export function postFormData(
  req,
  { formId, keepFormOpen, onComplete, onError }
) {
  return async (dispatch, getState) => {
    try {
      dispatch(FormActions.toggleFormSubmitting(formId))
      const item = await postJSON(req)
      dispatch(FormActions.toggleFormSubmitting(formId))
      dispatch(FormActions.updateFormErrors({ formId }))
      if (keepFormOpen) {
        const { fields } = getState().forms[formId]
        dispatch(FormActions.setInitialFormFields({ data: fields, formId }))
      } else {
        dispatch(ModalActions.closeModal(formId))
        dispatch(FormActions.removeForm(formId))
      }
      if (onComplete) onComplete(item.result)
    } catch (e) {
      dispatch(FormActions.toggleFormSubmitting(formId))
      dispatch(FormActions.updateFormErrors({ errors: e.message || e, formId }))
      if (onError) onError(e)
    }
  }
}

export function clearHiddenDependentFields({
  fields,
  fieldValidation,
  formId
}) {
  return (dispatch) => {
    for (const field in fields) {
      const validationProperties = fieldValidation[field]
      if (validationProperties && validationProperties.dependsOn) {
        if (isDependentFieldHidden(fields, validationProperties.dependsOn)) {
          dispatch(
            FormActions.setFormField({ key: field, value: null, formId })
          )
        }
      }
    }
  }
}

const populateNestedFields = ({ changes, fields }) => {
  let newFields = changes
  if (fields.name && changes.name)
    newFields = { ...newFields, name: fields.name }
  if (fields.location && changes.location)
    newFields = { ...newFields, location: fields.location }
  if (fields.demographics && changes.demographics)
    newFields = { ...newFields, demographics: fields.demographics }
  return newFields
}

const getFormValue = (key, fields, files) => {
  if (has(fields, key)) {
    return get(fields, key)
  }
  return get(files, key)
}
